import { useState, useMemo, useCallback, useRef, useEffect } from "react"
import { Result } from "../Models/Utility/Result"
import { hasValue, FetchState } from "../Models/Client/FetchState"
import { asError } from "../Helpers/Functions/error"

export interface UseAsyncCommand {
    wasSuccessful: boolean
    isProcessing: boolean
    perform?: (params?: any) => Promise<void>
    reset: () => void
}

/**
 * Takes a function that returns promise, and returns it in wrapped
 * object along with the flags to tell status of the promise resolution.
 */
export function useAsyncCommand(
    action: undefined | ((params?: any) => Promise<void>)
): UseAsyncCommand {
    const isUnmounted = useRef(false)
    const [state, setState] = useState<FetchState<undefined>>(undefined)
    const perform = useMemo(() => {
        if (state === "loading" || action === undefined) {
            return
        }

        return async (params?: any) => {
            try {
                setState("loading")
                await action(params)

                if (!isUnmounted.current) {
                    setState(Result.ok(undefined))
                }
            } catch (e) {
                if (!isUnmounted.current) {
                    setState(Result.error(asError(e).message))
                }
            }
        }
    }, [state, action])
    const reset = useCallback(() => setState(undefined), [])

    useEffect(() => {
        return () => {
            isUnmounted.current = true
        }
    }, [])

    return {
        perform,
        reset,
        wasSuccessful: hasValue(state) && !state.isError,
        isProcessing: state === "loading"
    }
}
